import React, { useEffect, useRef } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  getNotificationOpenTarget,
  markNotificationRead,
  markNormalizedNotificationRead,
} from '../../notificationUtils';

export const NotificationDetailsModal = ({
  show,
  notification,
  onClose,
  onMarkedRead,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const autoReadIds = useRef(new Set());
  const openTarget = getNotificationOpenTarget(notification);

  const { mutate: markRead } = useMutation(
    (notificationId) => markNotificationRead(notificationId),
    {
      onSuccess: () => {
        if (notification) {
          onMarkedRead?.(markNormalizedNotificationRead(notification));
        }

        queryClient.invalidateQueries('notificationSummary');
        queryClient.invalidateQueries('notifications');
      },
      onError: () => {
        if (notification?.id) {
          autoReadIds.current.delete(notification.id);
        }

        toast.error('Could not mark notification as read right now.', {
          position: 'top-center',
          autoClose: 3000,
        });
      },
    },
  );

  useEffect(() => {
    if (!show || !notification?.id || notification.isRead) {
      return;
    }

    if (autoReadIds.current.has(notification.id)) {
      return;
    }

    autoReadIds.current.add(notification.id);
    markRead(notification.id);
  }, [show, notification?.id, notification?.isRead, markRead]);

  const handleOpenContext = () => {
    if (!openTarget || !notification?.recordId) {
      return;
    }

    if (openTarget.type === 'property') {
      navigate('/PropertyDetails', {
        state: {
          query: {
            property: {
              id: notification.recordId,
            },
          },
          prevPath: {
            path: '/notifications',
          },
        },
      });
      onClose?.();
      return;
    }

    if (openTarget.type === 'buyer') {
      navigate('/MyClients', {
        state: {
          notificationBuyerId: notification.recordId,
          notificationBuyerRef: notification.recordRef,
        },
      });
      onClose?.();
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="property-modal notification-detail-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <span className="font-weight-bold">
            {notification?.title || 'Notification'}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <dl className="notification-detail-list mb-0">
          <dt>Task Type</dt>
          <dd>{notification?.taskTypeName || '-'}</dd>

          <dt>Reminder Text</dt>
          <dd>{notification?.reminderText || notification?.message || '-'}</dd>

          <dt>Record Ref</dt>
          <dd>{notification?.recordRef || '-'}</dd>

          <dt>Raised Date</dt>
          <dd>{notification?.raisedDate || '-'}</dd>
        </dl>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {openTarget ? (
          <Button variant="primary" onClick={handleOpenContext}>
            {openTarget.label}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
};
